import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getWrapperSx, innerWrapperSx, stackSx } from './styles.ts';

type CountdownTileVariant = 'default' | 'circle';

type CountdownTileProps = {
  totalSeconds: number;
  onComplete?: () => void;
  variant?: CountdownTileVariant;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const DEFAULT_START_AT_PERCENT = -6;
const DEFAULT_SIZE = 240;
const DEFAULT_CORNER = 72;

const CIRCLE_SIZE = 128;
const CIRCLE_RING_THICKNESS = 8;

export function CountdownTile({
                                totalSeconds,
                                onComplete,
                                variant = 'default',
                              }: CountdownTileProps) {
  const theme = useTheme();

  const [inner, setInner] = useState<number>(totalSeconds);
  const remaining = inner;

  // Reset when totalSeconds changes
  useEffect(() => {
    setInner(totalSeconds);
  }, [totalSeconds]);

  useEffect(() => {
    if (remaining <= 0) return;

    const id = window.setInterval(() => {
      setInner((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [remaining]);

  // Call onComplete only once per countdown end
  const completedRef = useRef(false);
  useEffect(() => {
    if (remaining === 0) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      return;
    }
    completedRef.current = false;
  }, [remaining, onComplete]);

  const total = Math.max(1, totalSeconds);
  const elapsedProgress = clamp01((total - Math.max(0, remaining)) / total);

  const circleStyles = useMemo(() => {
    if (variant !== 'circle') return null;

    const angle = elapsedProgress * 360;

    const active = theme.palette.accent;
    const rest = theme.palette.white_20;

    // "from 90deg" like in your SVG
    const ringBg = `conic-gradient(from 0deg,
      ${active} 0deg,
      ${active} ${angle}deg,
      ${rest} ${angle}deg,
      ${rest} 360deg
    )`;

    const mask = `radial-gradient(
      farthest-side,
      transparent calc(100% - ${CIRCLE_RING_THICKNESS}px),
      #000 calc(100% - ${CIRCLE_RING_THICKNESS}px + 1px)
    )`;

    return { ringBg, mask };
  }, [variant, elapsedProgress, theme.palette]);

  if (variant === 'circle' && circleStyles) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: circleStyles.ringBg,
            WebkitMask: circleStyles.mask,
            mask: circleStyles.mask,
            pointerEvents: 'none',
          }}
        />

        <Typography
          variant={'title'}
          sx={{
            position: 'relative',
            color: theme.palette.white_90,
          }}
        >
          {remaining}
        </Typography>
      </Box>
    );
  }


  const sw = Math.max(10, Math.round(DEFAULT_SIZE * 0.06));
  const pad = sw / 2;
  const rectSize = DEFAULT_SIZE - sw;

  // Align SVG rounding with container rounding
  const rx = Math.max(0, DEFAULT_CORNER - pad);

  const dash = elapsedProgress * 100;
  const gap = 100 - dash;

  return (
    <Box sx={getWrapperSx(theme, DEFAULT_SIZE, DEFAULT_CORNER)}>
      <Box
        sx={{
          position: 'absolute',
          inset: `${sw}px`,
          borderRadius: `${Math.max(0, DEFAULT_CORNER - sw)}px`,
          backgroundColor: 'white',
          px: 14,
          py: 8,
        }}
      />

      <Box component="svg" viewBox={`0 0 ${DEFAULT_SIZE} ${DEFAULT_SIZE}`} sx={innerWrapperSx}>
        <rect
          x={pad}
          y={pad}
          width={rectSize}
          height={rectSize}
          rx={rx}
          ry={rx}
          fill="none"
          stroke={theme.palette.accent}
          strokeWidth={sw}
          pathLength={100}
          strokeDasharray={`${dash} ${gap}`}
          strokeDashoffset={DEFAULT_START_AT_PERCENT}
          strokeLinejoin="round"
        />
      </Box>

      <Stack sx={stackSx}>
        <Typography variant={'accent_alt'}>{remaining}</Typography>
        <Typography variant={'title'}>SEC</Typography>
      </Stack>
    </Box>
  );
}
